
import { Helmet } from 'react-helmet-async';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';
import { useSEO } from '@/hooks/useSEO';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  image?: string;
  canonical?: string;
  url?: string;
  noindex?: boolean;
  structuredData?: any;
  pageType?: string;
  pageIdentifier?: string;
  type?: string;
  gameData?: any;
  lastModified?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  image,
  canonical,
  url,
  noindex = false,
  structuredData,
  pageType,
  pageIdentifier,
  type,
  gameData,
  lastModified
}) => {
  const { settings } = useWebsiteSettings();
  
  // Use the useSEO hook if pageType is provided
  const { data: seoData } = useSEO(pageType || 'website', pageIdentifier);
  
  // Use admin panel settings when available
  const siteName = settings?.site_name || 'TerraGamers';
  const metaTitle = title || seoData?.meta_title || settings?.meta_title || siteName;
  const metaDescription = description || seoData?.meta_description || settings?.meta_description || settings?.site_description || 'Discover and play thousands of free online games. Action, puzzle, racing, and more!';
  const metaKeywords = keywords || seoData?.meta_keywords || settings?.meta_keywords || 'games, online games, free games, browser games';
  const finalOgTitle = ogTitle || seoData?.og_title || metaTitle;
  const finalOgDescription = ogDescription || seoData?.og_description || metaDescription;
  const finalOgImage = ogImage || image || seoData?.og_image;
  const faviconUrl = settings?.favicon_url;

  // Generate structured data for games if gameData is provided
  const gameStructuredData = gameData ? {
    "@context": "https://schema.org",
    "@type": "Game",
    "name": gameData.title,
    "description": gameData.description,
    "image": gameData.thumbnail,
    "genre": gameData.category,
    "playCount": gameData.playCount,
    "dateCreated": gameData.createdAt,
    "dateModified": gameData.updatedAt || lastModified,
    "aggregateRating": gameData.rating ? {
      "@type": "AggregateRating",
      "ratingValue": gameData.rating,
      "bestRating": "5"
    } : undefined
  } : undefined;

  const finalStructuredData = structuredData || gameStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:type" content={type || "website"} />
      <meta property="og:site_name" content={siteName} />
      {finalOgImage && <meta property="og:image" content={finalOgImage} />}
      {url && <meta property="og:url" content={url} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      {finalOgImage && <meta name="twitter:image" content={finalOgImage} />}
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical URL */}
      {(canonical || url) && <link rel="canonical" href={canonical || url} />}
      
      {/* Last Modified */}
      {lastModified && <meta name="last-modified" content={lastModified} />}
      
      {/* Favicon */}
      {faviconUrl && (
        <>
          <link rel="icon" href={faviconUrl} />
          <link rel="shortcut icon" href={faviconUrl} />
          <link rel="apple-touch-icon" href={faviconUrl} />
        </>
      )}
      
      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
      
      {/* Google Analytics */}
      {settings?.google_analytics_id && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${settings.google_analytics_id}`} />
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${settings.google_analytics_id}');
            `}
          </script>
        </>
      )}
    </Helmet>
  );
};
